import { Link } from '@tanstack/react-router';
import { Wand2, Plus, ChevronRight } from 'lucide-react';
import { Button, cn, Paginator } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { useBlueprints, type ExerciseBlueprintRow } from '@/lib/blueprint/api';
import { usePagedList } from '@/lib/use-paged-list';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-xp-100 text-xp-700',
  draft: 'bg-ink-100 text-ink-600',
  archived: 'bg-accent-50 text-accent-600',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Actif',
  draft: 'Brouillon',
  archived: 'Archivé',
};

function BlueprintRow({ blueprint }: { blueprint: ExerciseBlueprintRow }) {
  return (
    <Link
      to="/tutor/blueprints/$blueprintId"
      params={{ blueprintId: blueprint.id }}
      className="hover:bg-ink-50 group flex min-h-[2.75rem] items-center gap-3 rounded-xl p-3 transition-colors active:scale-[0.99]"
    >
      <div className="bg-primary-50 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
        <Wand2 className="text-primary-500 h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-ink-900 truncate text-[14px] font-semibold">
          {blueprint.title}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <span className="text-ink-500 font-sans text-[11px]">
            {LEVEL_LABEL[blueprint.level] ?? blueprint.level}
          </span>
          <span className="text-ink-300">·</span>
          <span className="text-ink-500 font-sans text-[11px]">
            {blueprint.spec.parameters.length} paramètre
            {blueprint.spec.parameters.length !== 1 ? 's' : ''}
          </span>
          <span className="text-ink-300">·</span>
          <span className="text-ink-500 font-sans text-[11px]">v{blueprint.version}</span>
        </div>
      </div>
      <span
        className={cn(
          'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
          STATUS_STYLES[blueprint.status],
        )}
      >
        {STATUS_LABEL[blueprint.status] ?? blueprint.status}
      </span>
      <ChevronRight className="text-ink-300 h-4 w-4 shrink-0 group-hover:translate-x-0.5" />
    </Link>
  );
}

/**
 * Route tuteur `/tutor/blueprints` — liste des générateurs paramétriques
 * d'exercices. Chaque Blueprint est construit UNE FOIS par IA (segmentation
 * d'un corrigé fourni) puis pilote UN exercice paramétré dont les valeurs sont
 * tirées à chaque tentative d'élève (migration 0229) — sans
 * nouvel appel IA. Indépendant du système DAG solve-backward.
 */
export function TutorBlueprintsPage() {
  const blueprints = useBlueprints();
  const { items: pageBlueprints, paginator } = usePagedList(blueprints.data, {
    unit: 'générateurs',
    label: 'Pages des générateurs',
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="bg-primary-50 flex h-10 w-10 items-center justify-center rounded-xl">
            <Wand2 className="text-primary-500 h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-ink-900 text-xl font-extrabold leading-tight">
              Générateurs d&apos;exercices
            </h1>
            <p className="text-ink-500 text-xs">
              Un exercice réel → un exercice paramétré : mêmes raisonnements, nombres différents à
              chaque tentative
            </p>
          </div>
        </div>
        <Link to="/tutor/blueprints/new" className="w-full sm:w-auto">
          <Button fullWidth>
            <span className="flex items-center justify-center gap-2">
              <Plus className="h-4 w-4" /> Nouveau
            </span>
          </Button>
        </Link>
      </header>

      {blueprints.isLoading && <p className="text-ink-500 text-sm">Chargement…</p>}

      {blueprints.data && blueprints.data.length === 0 && (
        <div className="border-ink-200 text-ink-500 rounded-2xl border border-dashed bg-white p-6 text-center text-sm">
          Aucun générateur pour l&apos;instant. Crée le premier à partir d&apos;un exercice réel.
        </div>
      )}

      <div className="space-y-1">
        {pageBlueprints.map((blueprint) => (
          <BlueprintRow key={blueprint.id} blueprint={blueprint} />
        ))}
      </div>
      <Paginator {...paginator} />
    </div>
  );
}
